import { Checkbox, DefaultButton, Dropdown, MessageBar, MessageBarType, PrimaryButton, Spinner, Stack, Text, TextField } from "@fluentui/react"
import { useEffect, useState } from "react"
import { medicalRequest } from "../services/medicalQuestionnaireService"
import { MedicalApplication, MedicalCondition, MedicalDefinition, MedicalField, MedicalPersonDocument, MedicalReview, MedicalValue, MedicalValues } from "../types/medicalQuestionnaire"

const OPTION = 121640000
const groupFieldNames = ["cw_effectivedate", "cw_groupnumber", "cw_division", "cw_coverageselection"]
const emptyPerson = (): MedicalPersonDocument => ({ fields: {}, conditions: [], medications: [], otherCoverage: [], general: [] })
const styles = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 250px), 1fr))", gap: 16 }
const blank = (v: MedicalValue | undefined) => (v === undefined || v === null ? "" : String(v))

export function MedicalFields({ fields, values, onChange, disabled = false }: { fields: MedicalField[]; values: MedicalValues; onChange: (values: MedicalValues) => void; disabled?: boolean }) {
	return (
		<div style={styles}>
			{fields.map(field => {
				const value = values[field.name]
				const set = (value: MedicalValue) => onChange({ ...values, [field.name]: value })
				if (field.type === "Boolean")
					return (
						<Dropdown
							key={field.name}
							label={field.label}
							disabled={disabled}
							selectedKey={typeof value === "boolean" ? String(value) : null}
							options={[
								{ key: "true", text: "Yes" },
								{ key: "false", text: "No" }
							]}
							onChange={(_, item) => set(item.key === "true")}
							placeholder="Choose..."
						/>
					)
				if (field.options)
					return (
						<Dropdown
							key={field.name}
							label={field.label}
							disabled={disabled}
							multiSelect={field.type === "MultiSelectPicklist"}
							selectedKey={field.type !== "MultiSelectPicklist" ? (value as number) ?? null : undefined}
							selectedKeys={field.type === "MultiSelectPicklist" ? blank(value).split(",").filter(Boolean).map(Number) : undefined}
							options={field.options.map((text, index) => ({ key: OPTION + index, text }))}
							onChange={(_, item) => {
								if (field.type !== "MultiSelectPicklist") set(Number(item.key))
								else {
									const selected = new Set(blank(value).split(",").filter(Boolean))
									if (item.selected) selected.add(String(item.key))
									else selected.delete(String(item.key))
									set(Array.from(selected).join(","))
								}
							}}
							placeholder="Choose..."
						/>
					)
				return (
					<TextField
						key={field.name}
						label={field.label}
						disabled={disabled}
						value={blank(value)}
						multiline={field.type === "Memo"}
						rows={field.type === "Memo" ? 3 : undefined}
						maxLength={field.length}
						type={
							field.name === "cw_ssn" ? "password" : field.type === "Date" ? "date" : ["Integer", "Decimal"].includes(field.type) ? "number" : field.type === "Email" ? "email" : "text"
						}
						step={field.type === "Decimal" ? "0.01" : undefined}
						min={["Integer", "Decimal"].includes(field.type) ? 0 : undefined}
						onChange={(_, value) => set(value === "" ? null : ["Integer", "Decimal"].includes(field.type) ? Number(value) : value || null)}
					/>
				)
			})}
		</div>
	)
}

function Section({ title, children, open = false }: { title: string; children: React.ReactNode; open?: boolean }) {
	return (
		<details open={open || undefined} style={{ border: "1px solid #d6dfe7", borderRadius: 8, padding: 16, marginBottom: 14 }}>
			<summary style={{ cursor: "pointer", fontSize: 18, fontWeight: 600, padding: 4 }}>{title}</summary>
			<div style={{ paddingTop: 16 }}>{children}</div>
		</details>
	)
}
function Repeater({ title, fields, values, onChange, disabled }: { title: string; fields: MedicalField[]; values: MedicalValues[]; onChange: (values: MedicalValues[]) => void; disabled: boolean }) {
	return (
		<Stack tokens={{ childrenGap: 12 }}>
			{values.map((value, index) => (
				<Stack key={index} tokens={{ childrenGap: 8 }} styles={{ root: { padding: 12, border: "1px solid #ddd", borderRadius: 6 } }}>
					<Text variant="mediumPlus">
						{title} {index + 1}
					</Text>
					<MedicalFields fields={fields} values={value} disabled={disabled} onChange={v => onChange(values.map((row, i) => (i === index ? v : row)))} />
					<DefaultButton text={`Remove ${title.toLowerCase()} ${index + 1}`} disabled={disabled} onClick={() => onChange(values.filter((_, i) => i !== index))} />
				</Stack>
			))}
			<DefaultButton text={`Add ${title.toLowerCase()}`} disabled={disabled} onClick={() => onChange([...values, {}])} />
		</Stack>
	)
}

function DrugSearch({ onSelect, disabled }: { onSelect: (values: MedicalValues) => void; disabled: boolean }) {
	const [term, setTerm] = useState("")
	const [results, setResults] = useState<{ id: string; name: string; brand?: string; strength?: string }[]>([])
	const [error, setError] = useState("")
	const [searched, setSearched] = useState(false)
	const [busy, setBusy] = useState(false)
	async function search() {
		setBusy(true)
		setError("")
		try {
			setResults(await medicalRequest(`clinical/drugs?q=${encodeURIComponent(term)}`))
			setSearched(true)
		} catch (e) {
			setError(e instanceof Error ? e.message : "Medication lookup failed.")
		} finally {
			setBusy(false)
		}
	}
	return (
		<Stack tokens={{ childrenGap: 8 }}>
			<TextField label="Search the local medication catalog" value={term} disabled={disabled || busy} onChange={(_, v) => setTerm(v || "")} />
			<DefaultButton text="Search medications" disabled={disabled || busy || term.trim().length < 2} onClick={search} />
			{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
			{searched && !results.length && <Text>No matching catalog entries. Use manual medication entry below. No external service receives your medical answers.</Text>}
			{results.map(drug => (
				<DefaultButton
					key={drug.id}
					disabled={disabled}
					text={`${drug.name}${drug.brand ? ` (${drug.brand})` : ""} ${drug.strength || ""}`}
					onClick={() => onSelect({ cw_name: drug.name, cw_dosestrength: drug.strength || "", drugId: drug.id })}
				/>
			))}
		</Stack>
	)
}

function PersonEditor({
	definition,
	document,
	role,
	disabled,
	onChange
}: {
	definition: MedicalDefinition
	document: MedicalPersonDocument
	role: number
	disabled: boolean
	onChange: (v: MedicalPersonDocument) => void
}) {
	const table = (name: string, omitted: string[] = []) => definition.tables[name].filter(field => !omitted.includes(field.name))
	function condition(number: number): MedicalCondition {
		return document.conditions.find(c => c.number === number) || { number, fields: {}, details: [] }
	}
	function updateCondition(number: number, change: Partial<MedicalCondition>) {
		const next = { ...condition(number), ...change }
		onChange({ ...document, conditions: [...document.conditions.filter(c => c.number !== number), next].sort((a, b) => a.number - b.number) })
	}
	const waived = document.fields.cw_electionstatus === OPTION + 2
	const demographic = table("cw_coveredperson").filter(
		f => !["cw_electionstatus", "cw_waiverreason"].includes(f.name) && (role === OPTION + 2 || !["cw_isstepchild", "cw_iscustodialparent", "cw_iscourtorderedcoverage"].includes(f.name))
	)
	return (
		<Stack tokens={{ childrenGap: 12 }}>
			<Section title="Coverage election" open>
				<MedicalFields
					fields={table("cw_coveredperson").filter(f => ["cw_electionstatus", ...(waived ? ["cw_waiverreason"] : [])].includes(f.name))}
					values={document.fields}
					disabled={disabled}
					onChange={fields => onChange({ ...document, fields })}
				/>
				{waived && <MessageBar>Waiving coverage clears medical answers for this person when saved. Complete the household waiver and signature in Application details.</MessageBar>}
			</Section>
			<Section title="Personal information" open>
				<MedicalFields
					fields={waived ? demographic.filter(f => ["cw_firstname", "cw_lastname", "cw_dateofbirth"].includes(f.name)) : demographic}
					values={document.fields}
					disabled={disabled}
					onChange={fields => onChange({ ...document, fields })}
				/>
			</Section>
			{!waived && (
				<>
					<Section title="Other coverage">
						<Repeater
							title="Other coverage"
							fields={table("cw_othercoverage")}
							values={document.otherCoverage}
							disabled={disabled}
							onChange={otherCoverage => onChange({ ...document, otherCoverage })}
						/>
					</Section>
					<Section title={`Medical history (${document.conditions.filter(c => [OPTION + 1, OPTION + 2].includes(Number(c.fields.cw_response))).length}/56 answered)`} open>
						<Text>
							For each condition, disclose current treatment or a diagnosis/treatment history in the past five years. Answer for this person only. Do not infer pregnancy applicability
							from gender.
						</Text>
						{definition.conditions.map(item => {
							const answer = condition(item.number)
							const yes = answer.fields.cw_response === OPTION + 1
							return (
								<div key={item.number} style={{ padding: "14px 0", borderBottom: "1px solid #e4e8ed" }}>
									<Dropdown
										label={`${item.number}. ${item.name}`}
										placeholder="Choose Yes or No"
										disabled={disabled}
										selectedKey={(answer.fields.cw_response as number) ?? null}
										options={[
											{ key: OPTION + 1, text: "Yes" },
											{ key: OPTION + 2, text: "No" }
										]}
										onChange={(_, option) => updateCondition(item.number, { fields: { ...answer.fields, cw_response: Number(option.key) } })}
									/>
									{yes && (
										<Stack tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: 12, padding: 12, background: "#f5f8fa" } }}>
											<Text>{item.instructions}</Text>
											<MedicalFields
												fields={table("cw_conditionresponse", ["cw_response", ...(item.number !== 1 ? ["cw_bpreading", "cw_bpreadingdate"] : [])])}
												values={answer.fields}
												disabled={disabled}
												onChange={fields => updateCondition(item.number, { fields })}
											/>
											<Repeater
												title="Treatment detail"
												fields={table("cw_conditiondetail")}
												values={answer.details}
												disabled={disabled}
												onChange={details => updateCondition(item.number, { details })}
											/>
											{item.number === 16 && (
												<>
													<Text variant="large">Diabetes follow-up</Text>
													<MedicalFields
														fields={table("cw_diabetesdetail").filter(
															f =>
																!{
																	cw_soredescription: answer.diabetes?.cw_hasnonhealingsore !== true,
																	cw_heartdiseasedescription: answer.diabetes?.cw_hasheartdisease !== true,
																	cw_heartsurgerydescription: answer.diabetes?.cw_hashadheartsurgery !== true
																}[f.name]
														)}
														values={answer.diabetes || {}}
														disabled={disabled}
														onChange={diabetes => updateCondition(item.number, { diabetes })}
													/>
													<Dropdown
														label="Diabetes complications (select all that apply)"
														multiSelect
														disabled={disabled}
														selectedKeys={answer.complications || []}
														options={definition.tables.cw_diabetescomplication[0].options.map((text, i) => ({ key: OPTION + i, text }))}
														onChange={(_, option) =>
															updateCondition(item.number, {
																complications: option.selected
																	? [...(answer.complications || []), Number(option.key)]
																	: (answer.complications || []).filter(k => k !== option.key)
															})
														}
													/>
												</>
											)}
											{item.number === 55 && (
												<>
													<Text variant="large">Pregnancy follow-up</Text>
													<MedicalFields
														fields={table("cw_pregnancydetail")}
														values={answer.pregnancy || {}}
														disabled={disabled}
														onChange={pregnancy => updateCondition(item.number, { pregnancy })}
													/>
												</>
											)}
										</Stack>
									)}
								</div>
							)
						})}
					</Section>
					<Section title="Current prescribed medications">
						<DrugSearch disabled={disabled} onSelect={med => onChange({ ...document, medications: [...document.medications, med] })} />
						<Repeater
							title="Medication"
							fields={[...table("cw_medication", ["cw_drugcode", "cw_drugcodesystem"]), { name: "conditionNumber", label: "Related Yes condition number (optional)", type: "Integer" }]}
							values={document.medications}
							disabled={disabled}
							onChange={medications => onChange({ ...document, medications })}
						/>
					</Section>
					<Section title="General health">
						{definition.generalQuestions.map((question, index) => {
							const value = document.general.find(g => g.cw_questioncode === OPTION + index) || { cw_questioncode: OPTION + index }
							return (
								<Stack key={index} tokens={{ childrenGap: 8 }} styles={{ root: { marginBottom: 20 } }}>
									<Text variant="mediumPlus">{question}</Text>
									<MedicalFields
										fields={table("cw_generalhealthresponse").filter(
											f => f.name === "cw_response" || (value.cw_response === OPTION + 1 && (f.name === "cw_explanation" || (index === 0 && f.name === "cw_substanceused")))
										)}
										values={value}
										disabled={disabled}
										onChange={g => onChange({ ...document, general: [...document.general.filter(row => row.cw_questioncode !== OPTION + index), g] })}
									/>
								</Stack>
							)
						})}
					</Section>
				</>
			)}
		</Stack>
	)
}

export function MedicalQuestionnaireForm() {
	const [definition, setDefinition] = useState<MedicalDefinition>()
	const [application, setApplication] = useState<MedicalApplication>()
	const [list, setList] = useState<{ invitations: { id: string }[]; applications: { id: string; number: string; submittedAt?: string }[] }>()
	const [personId, setPersonId] = useState("")
	const [document, setDocument] = useState<MedicalPersonDocument>()
	const [review, setReview] = useState<MedicalReview>()
	const [activity, setActivity] = useState<"Loading" | "Saving" | null>("Loading")
	const busy = activity !== null
	const [error, setError] = useState("")
	const [dirty, setDirty] = useState(false)
	const [saved, setSaved] = useState("")
	const [signature, setSignature] = useState("")
	const [accepted, setAccepted] = useState(false)
	const [authorized, setAuthorized] = useState(false)
	const [syntheticOnly, setSyntheticOnly] = useState(false)
	const [authority, setAuthority] = useState(false)
	useEffect(() => {
		let active = true
		async function load() {
			try {
				const [def, items] = await Promise.all([
					medicalRequest<MedicalDefinition>("clinical/definition"),
					medicalRequest<typeof list>(
						`clinical/applications${new URLSearchParams(window.location.search).get("accountId") ? `?accountId=${encodeURIComponent(new URLSearchParams(window.location.search).get("accountId"))}` : ""}`
					)
				])
				if (!active) return
				setDefinition(def)
				setList(items)
				const params = new URLSearchParams(window.location.search)
				const id = params.get("application")
				const invitationId = params.get("invitation")
				if (id) {
					const loaded = await medicalRequest<MedicalApplication>(`clinical/applications/${encodeURIComponent(id)}`)
					if (active) setApplication(loaded)
				} else if (invitationId) {
					const result = await medicalRequest<{ id: string }>("clinical/applications", { invitationId })
					if (!active) return
					const loaded = await medicalRequest<MedicalApplication>(`clinical/applications/${encodeURIComponent(result.id)}`)
					if (active) setApplication(loaded)
				} else if (!params.has("accountId") && !window.location.pathname.replace(/\/$/, "").endsWith("/confirmation")) {
					if (items.applications.length === 1 && !items.invitations.length) {
						const loaded = await medicalRequest<MedicalApplication>(`clinical/applications/${encodeURIComponent(items.applications[0].id)}`)
						if (active) setApplication(loaded)
					} else if (!items.applications.length && items.invitations.length === 1) {
						const result = await medicalRequest<{ id: string }>("clinical/applications", { invitationId: items.invitations[0].id })
						if (!active) return
						const loaded = await medicalRequest<MedicalApplication>(`clinical/applications/${encodeURIComponent(result.id)}`)
						if (active) setApplication(loaded)
					}
				}
			} catch (e) {
				if (active)
					setError(
						e instanceof TypeError && e.message === "Failed to fetch"
							? "The questionnaire API could not be reached. Check the API connection and cross-origin preflight configuration, then reload."
							: e instanceof Error
								? e.message
								: "The questionnaire could not be loaded."
					)
			} finally {
				if (active) setActivity(null)
			}
		}
		void load()
		return () => {
			active = false
		}
	}, [])
	useEffect(() => {
		const beforeUnload = (e: BeforeUnloadEvent) => {
			if (dirty) {
				e.preventDefault()
				e.returnValue = ""
			}
		}
		window.addEventListener("beforeunload", beforeUnload)
		return () => window.removeEventListener("beforeunload", beforeUnload)
	}, [dirty])
	async function perform(action: () => Promise<void>, operation: "Loading" | "Saving" = "Loading") {
		setActivity(operation)
		setError("")
		try {
			await action()
		} catch (e) {
			setError(e instanceof Error ? e.message : "The questionnaire request failed.")
		} finally {
			setActivity(null)
		}
	}
	async function open(id: string) {
		await perform(async () => {
			setApplication(await medicalRequest(`clinical/applications/${id}`))
			setPersonId("")
			setDocument(undefined)
			setReview(undefined)
			setDirty(false)
		})
	}
	async function start(invitationId: string) {
		await perform(async () => {
			const result = await medicalRequest<{ id: string }>("clinical/applications", { invitationId })
			setApplication(await medicalRequest(`clinical/applications/${result.id}`))
		})
	}
	async function save() {
		if (!application) return
		await perform(async () => {
			const result = personId
				? await medicalRequest<MedicalApplication>(`clinical/applications/${application.id}/persons/${personId}`, { version: application.version, document }, true, undefined, "PUT")
				: await medicalRequest<MedicalApplication>(
						`clinical/applications/${application.id}`,
						{ version: application.version, fields: Object.fromEntries(Object.entries(application.fields).filter(([name]) => !groupFieldNames.includes(name))) },
						true,
						undefined,
						"PUT"
					)
			setApplication(result)
			setDirty(false)
			setSaved(new Date().toLocaleTimeString())
			setReview(undefined)
		}, "Saving")
	}
	async function choosePerson(id: string) {
		await perform(async () => {
			if (!id) {
				setPersonId("")
				setDocument(undefined)
				setReview(undefined)
				return
			}
			const result = await medicalRequest<{ document: MedicalPersonDocument; version: string }>(`clinical/applications/${application.id}/persons/${id}`)
			setPersonId(id)
			setDocument({ ...emptyPerson(), ...result.document })
			setApplication(current => ({ ...current, version: result.version }))
			setReview(undefined)
		})
	}
	const disabled = busy || application?.readOnly
	const reviewErrors = review ? [...review.errors, ...review.persons.flatMap(p => p.errors.map(error => `${p.name}: ${error}`))] : []
	function backToEditing() {
		setReview(undefined)
		setAccepted(false)
		setAuthorized(false)
		setSyntheticOnly(false)
		setSignature("")
	}
	return (
		<main style={{ maxWidth: 1100, margin: "24px auto", padding: 20 }}>
			<Stack tokens={{ childrenGap: 18 }}>
				<Text as="h1" variant="xxLarge">
					Household medical questionnaire
				</Text>
				{definition?.sandboxOnly && (
					<MessageBar messageBarType={MessageBarType.warning}>
						SANDBOX TEST ONLY. Privacy/consent text is a sample. Use synthetic names, SSNs and medical answers only. This is not a production insurance application.
					</MessageBar>
				)}
				{busy && !application && <Spinner label={`${activity} questionnaire...`} />}
				{error && !application && (
					<MessageBar messageBarType={MessageBarType.error} isMultiline>
						{error}
						{dirty && " Your unsaved answers remain on screen. For a version conflict, copy no medical data to unapproved storage; reload and re-enter your changes."}
					</MessageBar>
				)}
				{!application && list && (
					<>
						<Text>
							After email verification and sample privacy acceptance, register or sign in to the Contact linked to your employee household record. A different signed-in account cannot
							access this application.
						</Text>
						{list.applications.map(item => (
							<PrimaryButton key={item.id} text={`${item.submittedAt ? "View receipt" : "Resume"} ${item.number}`} disabled={busy} onClick={() => open(item.id)} />
						))}
						{list.invitations.map(item => (
							<DefaultButton key={item.id} text="Start or resume assigned household questionnaire" disabled={busy} onClick={() => start(item.id)} />
						))}
						{!list.applications.length && !list.invitations.length && (
							<MessageBar>No questionnaires are assigned to your signed-in Contact. Complete the personal email link, or contact ACMG if you signed in with another account.</MessageBar>
						)}
					</>
				)}
				{application && definition && (
					<>
						<Text variant="large">
							{application.number || "New application"}
							{application.submittedAt ? ` — Submitted ${new Date(application.submittedAt).toLocaleString()}` : ""}
						</Text>
						{application.submittedAt && (
							<MessageBar messageBarType={MessageBarType.success}>
								Your questionnaire was submitted and is read-only. {definition.sandboxOnly && "This is a test receipt, not confirmation of insurance coverage."}
							</MessageBar>
						)}
						{!application.readOnly && !review && (
							<Checkbox
								label="I am the employee or authorized parent/guardian and have permission from each adult household member to enter their answers."
								checked={authority}
								onChange={(_, checked) => setAuthority(!!checked)}
							/>
						)}
						{!review && (
							<Dropdown
								label="Application section or household member"
								selectedKey={personId}
								disabled={busy || dirty || (!application.readOnly && !authority)}
								options={[
									{ key: "", text: "Application details and waiver" },
									...application.persons.map(p => ({ key: p.id, text: `${p.name} — ${["Employee", "Spouse", "Child", "Other dependent"][p.role - OPTION]}` }))
								]}
								onChange={(_, item) => choosePerson(String(item.key))}
							/>
						)}
						{dirty && <MessageBar>Unsaved changes. Save before switching people or reviewing.</MessageBar>}
						{!review &&
							(!authority && !application.readOnly ? (
								<MessageBar>Confirm your household authority above to begin.</MessageBar>
							) : personId && document ? (
								<PersonEditor
									definition={definition}
									document={document}
									role={application.persons.find(p => p.id === personId).role}
									disabled={disabled}
									onChange={v => {
										setDocument(v)
										setDirty(true)
									}}
								/>
							) : (
								<Section title="Application details and household waiver" open>
									<Text>These details come from your group and employee records. Contact ACMG if a value is missing or incorrect.</Text>
									<MedicalFields
										fields={definition.tables.cw_questionnairesubmission.filter(f => groupFieldNames.includes(f.name))}
										values={application.fields}
										disabled
										onChange={() => undefined}
									/>
									<MedicalFields
										fields={definition.tables.cw_questionnairesubmission.filter(f => {
											if (groupFieldNames.includes(f.name)) return false
											const reasons = String(application.fields.cw_applicationreasons || "").split(",")
											if (f.name === "cw_dateofhire") return reasons.includes(String(OPTION))
											if (["cw_qualifyingevent", "cw_qualifyingeventdate"].includes(f.name)) return reasons.includes(String(OPTION + 1))
											if (f.name === "cw_waiverotherreason")
												return (
													application.fields.cw_iswaiving === true &&
													String(application.fields.cw_waiverreasons || "")
														.split(",")
														.includes(String(OPTION + 5))
												)
											return !["cw_waivedpersons", "cw_waiverreasons", "cw_waiversignature", "cw_waiversigneddate"].includes(f.name) || application.fields.cw_iswaiving === true
										})}
										values={application.fields}
										disabled={disabled}
										onChange={fields => {
											setApplication({ ...application, fields })
											setDirty(true)
										}}
									/>
								</Section>
							))}
						{review && (
							<>
								<Text as="h2" variant="xLarge">
									Review your household answers
								</Text>
								{reviewErrors.length > 0 && (
									<MessageBar messageBarType={MessageBarType.error} isMultiline>
										<Text as="strong">Changes required before you can sign.</Text>
										<Text as="p">
											Use the edit buttons below to correct the listed answers. Save your changes, then select Review and sign again. Attestation and signature are unavailable
											until all required answers are complete.
										</Text>
										<ul>
											{reviewErrors.map((e, i) => (
												<li key={i}>{e}</li>
											))}
										</ul>
									</MessageBar>
								)}
								{!application.readOnly && review.errors.length > 0 && (
									<DefaultButton
										text="Edit application details and waiver"
										disabled={busy}
										onClick={() => {
											backToEditing()
											void choosePerson("")
										}}
									/>
								)}
								{!application.readOnly &&
									review.persons
										.filter(p => p.errors.length > 0)
										.map(p => (
											<DefaultButton
												key={p.id}
												text={`Edit answers for ${p.name}`}
												disabled={busy}
												onClick={() => {
													backToEditing()
													void choosePerson(p.id)
												}}
											/>
										))}
								<Section title="Application summary">
									<MedicalFields fields={definition.tables.cw_questionnairesubmission} values={review.fields} disabled onChange={() => undefined} />
								</Section>
								{review.persons.map(p => (
									<Section key={p.id} title={p.name}>
										<PersonEditor definition={definition} document={p.document} role={p.role} disabled onChange={() => undefined} />
									</Section>
								))}
								{!application.readOnly && reviewErrors.length === 0 && (
									<>
										<MessageBar messageBarType={MessageBarType.success}>										All required answers are complete. Review your answers above, then attest and sign to submit.</MessageBar>
										<Text>{definition.authorizationText}</Text>
										<Checkbox label="I accept the attestation shown above." checked={accepted} disabled={busy} onChange={(_, checked) => setAccepted(!!checked)} />
										<Checkbox
											label="I have authorization from each adult and am the parent/guardian for minors whose answers I entered."
											checked={authorized}
											disabled={busy}
											onChange={(_, checked) => setAuthorized(!!checked)}
										/>
										{definition.sandboxOnly && (
											<Checkbox
												label="All answers are synthetic test data. I understand this does not enroll anyone in insurance."
												checked={syntheticOnly}
												disabled={busy}
												onChange={(_, checked) => setSyntheticOnly(!!checked)}
											/>
										)}
										<TextField label="Typed employee signature" disabled={busy} value={signature} onChange={(_, v) => setSignature(v || "")} maxLength={250} />
										<PrimaryButton
											text="Sign and submit questionnaire"
											disabled={
												busy ||
												dirty ||
												!signature.trim() ||
												!accepted ||
												!authorized ||
												(definition.sandboxOnly && !syntheticOnly) ||
												!!review.errors.length ||
												review.persons.some(p => p.errors.length > 0)
											}
											onClick={() =>
												perform(async () => {
													const receipt = await medicalRequest<{ id: string }>(`clinical/applications/${application.id}/submit`, {
														version: review.version,
														signature,
														accepted,
														authorized,
														syntheticOnly
													})
													window.location.assign(`/medical-questionnaire/confirmation/?application=${encodeURIComponent(receipt.id)}`)
												}, "Saving")
											}
										/>
									</>
								)}
							</>
						)}
						<Stack
							role="region"
							aria-label="Questionnaire actions and save status"
							tokens={{ childrenGap: 10 }}
							styles={{
								root: {
									position: "sticky",
									bottom: 0,
									zIndex: 10,
									background: "#fff",
									borderTop: "1px solid #d6dfe7",
									padding: 16,
									boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
									maxHeight: "50vh",
									overflowY: "auto"
								}
							}}>
							{busy && <Spinner label={`${activity} questionnaire...`} />}
							{review && reviewErrors.length > 0 && (
								<MessageBar messageBarType={MessageBarType.error}>Changes required: correct the {reviewErrors.length} listed issues and save before signing.</MessageBar>
							)}
							{error && (
								<MessageBar messageBarType={MessageBarType.error} isMultiline>
									{error}
									{dirty && " Your changes have not been saved. Your unsaved answers remain on screen."}
								</MessageBar>
							)}
							<div role="status" aria-live="polite">
								{saved && !error && !busy && (
									<Text>
										Last saved at {saved}. {dirty ? "New changes are not saved." : "Draft saved successfully."}
									</Text>
								)}
							</div>
							<Stack horizontal wrap tokens={{ childrenGap: 10 }}>
								{review && (
									<DefaultButton
										text={application.readOnly ? "Back to questionnaire" : reviewErrors.length ? "Back to editing to fix required answers" : "Back to editing"}
										disabled={busy}
										onClick={backToEditing}
									/>
								)}
								{!review && !application.readOnly && <PrimaryButton text="Save draft" disabled={busy || !dirty || !authority} onClick={save} />}
								{!review && (
									<DefaultButton
										text={application.readOnly ? "View signed answers" : "Review and sign"}
										disabled={busy || dirty || (!application.readOnly && !authority)}
										onClick={() =>
											perform(async () => {
												setReview(await medicalRequest(`clinical/applications/${application.id}/review`))
											})
										}
									/>
								)}
							</Stack>
						</Stack>
					</>
				)}
			</Stack>
		</main>
	)
}
